/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/display-name */
import React, { useCallback, useContext, useMemo, Fragment } from 'react';
import { Table, Pagination, Popover, Icon } from 'grab-ui';
import PropTypes from 'prop-types';
import cx from 'classnames';
import I18n from 'i18n-js';
import get from 'lodash/get';
import moment from 'moment';
import formatCurrency from 'common/utils/format-currency';
import {
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
  SCRIBE_SHOPIFY_STATES,
} from 'applications/scribe/instrumentations/track';
import currencyFormats from 'constants/currency-constants';
import styles from './styles.less';
import {
  findDropOffPOIID,
  findPickUpPOIID,
  getAddress,
  getFulfillmentOrderWeightInKg,
  getUniqueOrderIDs,
  multipleAssignedLocationIDs,
} from './utils';
import { ORDER_TABLE_PAGE_SIZE, FINANCIAL_STATUS } from './consts';
import ShopifyDetailsPopover from '../shopify-details-popover';
import ShopifyTrackEventContext from '../track-event';
import useOrderItemOnboarding from './useOrderItemOnboarding';

const WarningPopover = ({ content }) => (
  <Popover placement="topRight" content={content} trigger="hover">
    <Icon type="exclamation-circle" className={cx(styles.warningIcon)} />
  </Popover>
);

WarningPopover.propTypes = {
  content: PropTypes.string.isRequired,
};

const DisabledFulfillmentOrderPopover = ({ reason }) => (
  <WarningPopover content={I18n.t(`shopify.orders.${reason}Warning`)} />
);

DisabledFulfillmentOrderPopover.propTypes = {
  reason: PropTypes.string.isRequired,
};

const AddressDetails = ({
  fulfillmentOrder,
  addressEntity,
  editFulfillmentOrderAddress,
  trackAddressHoverPopup,
}) => {
  const editHandler = useCallback(
    () => {
      editFulfillmentOrderAddress({
        fulfillmentOrder,
        addressEntity,
      });
      trackAddressHoverPopup(
        fulfillmentOrder,
        SCRIBE_SHOPIFY_EVENTS.EDIT_ADDRESS,
      );
    },
    [editFulfillmentOrderAddress, fulfillmentOrder, addressEntity],
  );

  return (
    <div className={cx(styles.detailsContent, styles.addressDetails)}>
      <div className={styles.section}>
        <div className={styles.header}>
          {I18n.t('shopify.orders.addressDetails.originalAddress')}
        </div>
        <div>
          {getAddress(fulfillmentOrder, {
            withCountryCity: true,
            withZip: true,
          })}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.header}>
          {I18n.t('shopify.orders.addressDetails.adjustedTo')}
        </div>
        <div>{get(addressEntity, 'address')}</div>
        <div className={styles.header}>
          {I18n.t('shopify.orders.addressDetails.locationDetails')}
        </div>
        {get(addressEntity, 'instruction') && (
          <div>{get(addressEntity, 'instruction')}</div>
        )}
        <div className={styles.addressActions}>
          {
            // eslint-disable-next-line
            <a onClick={editHandler}>
              {I18n.t('shopify.orders.addressDetails.editAddress')}
            </a>
          }
        </div>
      </div>
    </div>
  );
};

AddressDetails.propTypes = {
  fulfillmentOrder: PropTypes.shape({}).isRequired,
  addressEntity: PropTypes.shape({}).isRequired,
  editFulfillmentOrderAddress: PropTypes.func.isRequired,
  trackAddressHoverPopup: PropTypes.func.isRequired,
};

const TableView = ({
  shop,
  filter,
  metadata,
  onPageChange,
  ordersLoading,
  selectedCity,
  fulfillmentOrders,
  addressEntityMap,
  editFulfillmentOrderAddress,
  editingFulfillmentOrder,
  selectedFulfillmentOrders,
  onSelectFulfillmentOrder,
}) => {
  const { fireShopifyTrackEvent } = useContext(ShopifyTrackEventContext);

  const onSelectAll = useCallback(
    (selectedAll, _, changed) => {
      const hasChange = changed && changed.filter(o => !o.disabled).length > 0;
      if (selectedAll && hasChange) {
        fireShopifyTrackEvent({
          stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.SELECT_ALL,
          facets: {
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: getUniqueOrderIDs(
              fulfillmentOrders.filter(o => !o.disabled),
            ),
          },
        });
      }
    },
    [fireShopifyTrackEvent, fulfillmentOrders],
  );

  const onSelect = useCallback(
    (fulfillmentOrder, selected) => {
      if (selected) {
        fireShopifyTrackEvent({
          stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.SELECT_ORDER,
          facets: {
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: fulfillmentOrder.orderID,
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_DATETIME]:
              fulfillmentOrder.createdAt,
            [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: findPickUpPOIID(
              shop,
              fulfillmentOrder,
            ),
            [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: findDropOffPOIID(
              addressEntityMap,
              fulfillmentOrder,
            ),
            [SCRIBE_SHOPIFY_PARAMS.TOTAL_ITEMS]: get(fulfillmentOrder, [
              'lineItems',
              'length',
            ]),
            [SCRIBE_SHOPIFY_PARAMS.TOTAL_PRICE]: get(
              fulfillmentOrder,
              'subtotalPrice',
              0,
            ),
            [SCRIBE_SHOPIFY_PARAMS.ITEM_WEIGHT]: getFulfillmentOrderWeightInKg(
              fulfillmentOrder,
            ),
            [SCRIBE_SHOPIFY_PARAMS.SHIPPING_TYPE]: get(
              fulfillmentOrder,
              'shippingType',
            ),
          },
        });
      }
    },
    [fireShopifyTrackEvent, shop, addressEntityMap],
  );

  const trackAddressHoverPopup = useCallback(
    (fulfillmentOrder, eventName) => {
      fireShopifyTrackEvent({
        stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ADDRESS_HOVER_WEB,
        eventName,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: findPickUpPOIID(
            shop,
            fulfillmentOrder,
          ),
          [SCRIBE_SHOPIFY_PARAMS.DROPOFF_POI_ID]: findDropOffPOIID(
            addressEntityMap,
            fulfillmentOrder,
          ),
          [SCRIBE_SHOPIFY_PARAMS.LOCATION_DETAILS]: getAddress(
            fulfillmentOrder,
            {
              withAddress2: true,
              withCountryCity: true,
              withZip: true,
            },
          ),
        },
      });
    },
    [fireShopifyTrackEvent, addressEntityMap, shop],
  );

  const onAddressHoverPopup = useCallback(
    fulfillmentOrder => visible => {
      if (visible) {
        trackAddressHoverPopup(fulfillmentOrder, SCRIBE_SHOPIFY_EVENTS.DEFAULT);
      }
    },
    [trackAddressHoverPopup],
  );

  const {
    getOrderItemOnboardingElement,
    setOrderItemHoverOnboardingSuppress,
  } = useOrderItemOnboarding(fulfillmentOrders);

  const columns = useMemo(
    () => {
      const multiplePickupAddress = multipleAssignedLocationIDs(
        fulfillmentOrders,
      );

      const columns = [
        {
          title: I18n.t('shopify.orders.tableHeader.orderID'),
          key: 'orderID',
          render: (_, record, index) => <div>{record.orderName}</div>,
        },
        {
          title: I18n.t('shopify.orders.tableHeader.orderDate'),
          key: 'orderDate',
          render: (_, record, index) => (
            // Fri, Jul 5 2021 at 3:00pm
            <div>
              {moment(record.createdAt).format('ddd, MMM DD YYYY [at] h:mm a')}
            </div>
          ),
        },
        {
          title: I18n.t('shopify.orders.tableHeader.customerName'),
          key: 'customerName',
          render: (_, record, index) => (
            <div className={styles.fieldCustomerName}>
              {get(record, ['shippingAddress', 'name'])}
            </div>
          ),
        },
        {
          title: I18n.t('shopify.orders.tableHeader.item'),
          key: 'item',
          render: (_, record) => {
            const totalCount = record.lineItems.reduce(
              (count, item) => count + item.quantity,
              0,
            );
            const code = get(record, 'lineItems[0].currency');

            const elementID = `shopifyOrderItemPrice-${record.ID}`;

            return (
              <div>
                <ShopifyDetailsPopover
                  fulfillmentOrder={record}
                  onVisibleChange={() => setOrderItemHoverOnboardingSuppress()}
                >
                  <div id={elementID}>
                    {totalCount} item{totalCount > 1 && 's'} •{' '}
                    {currencyFormats.find(f => f.code === code)
                      ? formatCurrency({
                          code,
                          amount: record.subtotalPrice,
                        })
                      : `${code} ${record.subtotalPrice}`}
                  </div>
                </ShopifyDetailsPopover>
                {!record.disabled &&
                  getOrderItemOnboardingElement(record, elementID)}
              </div>
            );
          },
        },
        {
          title: multiplePickupAddress
            ? I18n.t('shopify.orders.tableHeader.shippingAddress')
            : I18n.t('shopify.orders.tableHeader.address'),
          key: 'shippingAddress',
          render: (_, record, index) => (
            <Popover
              placement="bottom"
              content={
                get(editingFulfillmentOrder, 'ID') !== record.ID && (
                  <AddressDetails
                    fulfillmentOrder={record}
                    addressEntity={addressEntityMap[record.ID]}
                    editFulfillmentOrderAddress={editFulfillmentOrderAddress}
                    trackAddressHoverPopup={trackAddressHoverPopup}
                  />
                )
              }
              trigger="hover"
              onVisibleChange={onAddressHoverPopup(record)}
              overlayClassName={styles.detailsPopover}
            >
              <div className={styles.fieldShippingAddress}>
                {getAddress(record, { withCountryCity: true, withZip: true })}
              </div>
            </Popover>
          ),
        },
        {
          title: I18n.t('shopify.orders.tableHeader.itemWeight'),
          key: 'itemWeight',
          render: (_, record, index) => (
            <div>{getFulfillmentOrderWeightInKg(record)}kg</div>
          ),
        },
        {
          title: I18n.t('shopify.orders.tableHeader.shippingType'),
          key: 'shippingType',
          render: (_, record, index) => <div>{record.shippingType}</div>,
        },
        {
          title: '',
          key: 'warning',
          className: cx(styles.warningColumn),
          render: (_, record) =>
            record.disabled ? (
              <DisabledFulfillmentOrderPopover reason={record.reason} />
            ) : (
              <Fragment />
            ),
        },
      ];

      if (multiplePickupAddress) {
        columns.splice(4, 0, {
          title: I18n.t('shopify.orders.tableHeader.pickupAddress'),
          key: 'pickupAddress',
          render: (_, record, index) =>
            get(
              shop.pickUpAddresses.find(
                adr => adr.shopifyLocationID === record.assignedLocationID,
              ),
              ['shopifyLocation', 'name'],
            ),
        });
      }

      if (get(selectedCity, 'countryCode') === 'PH') {
        columns.push({
          title: I18n.t('shopify.orders.tableHeader.financialStatus'),
          key: 'financialStatus',
          render: (_, record, index) => (
            <div>
              {record.financialStatus === FINANCIAL_STATUS.PAID
                ? I18n.t('shopify.orders.financialStatus.paid')
                : I18n.t('shopify.orders.financialStatus.pending')}{' '}
            </div>
          ),
        });
      }

      return columns;
    },
    [
      shop,
      selectedCity,
      fulfillmentOrders,
      addressEntityMap,
      editFulfillmentOrderAddress,
      editingFulfillmentOrder,
      onAddressHoverPopup,
    ],
  );

  return (
    <div className={styles.tableView}>
      <Table
        className={styles.table}
        rowClassName={record => record.disabled && styles.disabledOrder}
        loading={ordersLoading}
        rowSelection={{
          selectedRowKeys: selectedFulfillmentOrders.map(o => o.ID),
          onChange: onSelectFulfillmentOrder,
          onSelect,
          onSelectAll,
        }}
        columns={columns}
        rowKey={fulfillmentOrder => fulfillmentOrder.ID}
        pagination={false}
        dataSource={fulfillmentOrders}
      />
      <Pagination
        className={styles.pagination}
        pageSize={ORDER_TABLE_PAGE_SIZE}
        onChange={(page, pageSize) => {
          setOrderItemHoverOnboardingSuppress();
          onPageChange(page, pageSize);
        }}
        current={get(filter, 'page', 1)}
        total={get(metadata, 'totalOrders', 0)}
      />
    </div>
  );
};

TableView.propTypes = {
  shop: PropTypes.shape({}).isRequired,
  fulfillmentOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedFulfillmentOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filter: PropTypes.shape({}).isRequired,
  metadata: PropTypes.shape({}).isRequired,
  onPageChange: PropTypes.func.isRequired,
  ordersLoading: PropTypes.bool.isRequired,
  onSelectFulfillmentOrder: PropTypes.func.isRequired,
  selectedCity: PropTypes.shape({}).isRequired,
  addressEntityMap: PropTypes.shape({}).isRequired,
  editFulfillmentOrderAddress: PropTypes.func.isRequired,
  editingFulfillmentOrder: PropTypes.shape({}),
};

TableView.defaultProps = {
  editingFulfillmentOrder: undefined,
};

export default TableView;
